import React, { useState } from 'react';

import { useAppContext } from 'src/components/app-context';
import { useOfferListFilterContext } from '../offer-list-filter-context';
import { useProductContext } from '../product-context';

import { ButtonIcon, OutlinedButton } from '@prisjakt/ui/button/variants';
import { IconDropdownDown } from '@prisjakt/ui/icon/dropdown-down';
import { IconDropdownUp } from '@prisjakt/ui/icon/dropdown-up';
import { ModalBody, ModalFooter } from '@prisjakt/ui/modal';
import { Behavior, TitleSmallText } from '@prisjakt/ui/text/variants';
import Modal from 'src/components/modal-v2';
import { SelectionHighlightedText } from '../product-variants/styles-common';

import { getSplitOfferlist } from '../price-list/price-list-panel/utils/split-list';
import { getVariantSelections } from './utils';

import type { IFilterComponent } from './types';
import { FilterUIPlacementEnum } from 'src/components/price-list/types';

import {
  StyledClearButton,
  StyledFooterInner,
  StyledIconImageText,
  StyledModalInner,
  StyledPrimaryButton,
} from './styles';

export const ModalView: React.FC<IModalView> = props => {
  const { components } = props;
  const { market, t } = useAppContext();
  const { getActiveFilters, getFilter, resetFiltersByPlacement } =
    useOfferListFilterContext();
  const { offers } = useProductContext();
  const selections = getVariantSelections(t, getFilter);
  const [visible, setVisible] = useState<boolean>(false);

  const closeHandler = () => setVisible(false);
  const toggleHandler = () => setVisible(!visible);

  const filterHits = getSplitOfferlist(
    offers.filteredOffers,
    getActiveFilters()
  ).mainList.length;

  const renderButtonLabel = () => {
    const singleComponentWithCustomButton =
      components.length === 1 && components[0].buttonLabelComponent;

    if (singleComponentWithCustomButton) {
      // For single variant selection
      return (
        <>
          {singleComponentWithCustomButton}
          <ButtonIcon>
            {visible ? <IconDropdownUp /> : <IconDropdownDown />}
          </ButtonIcon>
        </>
      );
    }
    return (
      <StyledIconImageText>
        <img
          src={`${market.cdn}/g/rfe/illustrations/icon-color-wheel-20x20px.png`}
          srcSet={`${market.cdn}/g/rfe/illustrations/icon-color-wheel-20x20px.png 1x, ${market.cdn}/g/rfe/illustrations/icon-color-wheel-40x40px.png 2x`}
          alt=""
        />
        <TitleSmallText behavior={Behavior.OnelineEllipsis}>
          {t('product', 'variants.select-variants')}
          {Boolean(selections) && (
            <SelectionHighlightedText> ({selections})</SelectionHighlightedText>
          )}
        </TitleSmallText>
      </StyledIconImageText>
    );
  };

  return (
    <>
      <OutlinedButton onClick={toggleHandler} fullWidth selectable>
        {renderButtonLabel()}
      </OutlinedButton>

      {visible && (
        <Modal
          isOpen
          onRequestClose={closeHandler}
          title={t('product', 'variants.select-variants')}
        >
          <>
            <ModalBody>
              <StyledModalInner>
                {components.map(component => (
                  <div key={component.key}>{component.selectorComponent}</div>
                ))}
              </StyledModalInner>
            </ModalBody>
            <ModalFooter>
              <StyledFooterInner>
                <StyledClearButton
                  onClick={() =>
                    resetFiltersByPlacement(
                      FilterUIPlacementEnum.PRODUCT_HEADER
                    )
                  }
                >
                  {t('common', 'button.clear')}
                </StyledClearButton>
                <StyledPrimaryButton onClick={closeHandler}>
                  {t('product', 'prices.filter-offers-count', {
                    n: filterHits ? String(filterHits) : '0',
                  })}
                </StyledPrimaryButton>
              </StyledFooterInner>
            </ModalFooter>
          </>
        </Modal>
      )}
    </>
  );
};

interface IModalView {
  components: IFilterComponent[];
}
